/* Shared vocabulary for the Dataset section.
 *
 * The nature taxonomy is authored in the backend at load time (brief §6); the
 * UI only needs a label and a colour per nature, plus the tree/chip widgets
 * that the Summary and Explorer tabs both use.
 */
import React, { useMemo, useState } from 'react';

export const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

// Order matters: it is the order natures are listed in every chip row and tree.
export const NATURES = [
  { id: 'generation',       label: 'Generation',   color: '#4A90B8' },
  { id: 'production',       label: 'Production',   color: '#4ecca3' },
  { id: 'storage_soc',      label: 'Storage',      color: '#7B68AE' },
  { id: 'battery_flow',     label: 'Battery',      color: '#D4A843' },
  { id: 'consumption_el',   label: 'Consumption',  color: '#e07a5f' },
  { id: 'consumption_feed', label: 'Feed',         color: '#c98a6b' },
  { id: 'byproduct',        label: 'By-product',   color: '#3CC0C9' },
  { id: 'exchange',         label: 'Exchange',     color: '#60a5fa' },
  { id: 'demand',           label: 'Demand',       color: '#f472b6' },
  { id: 'loss',             label: 'Loss',         color: '#f87171' },
  { id: 'unassigned',       label: 'Unassigned',   color: '#94a3b8' },
];

export const NATURE_IDS = NATURES.map((n) => n.id);

const NATURE_INDEX = NATURES.reduce((m, n) => { m[n.id] = n; return m; }, {});

export const natureMeta = (id) => NATURE_INDEX[id] || { id: id || 'unassigned', label: id || 'unassigned', color: '#94a3b8' };

// Fixed 12-colour categorical palette, assigned in order of addition to a group.
export const PALETTE = [
  '#4ecca3', '#4A90B8', '#D4A843', '#7B68AE', '#3CC0C9', '#f472b6',
  '#f59e0b', '#60a5fa', '#a3e635', '#fb7185', '#22d3ee', '#c084fc',
];

// Grafana-style panel chrome — identical tokens to FrequencyDataView.
export const PANEL_BG      = '#181b1f';
export const PANEL_BG_PLOT = '#111217';
export const PANEL_BORDER  = 'rgba(255,255,255,0.07)';
export const PANEL_GRID    = 'rgba(255,255,255,0.08)';
export const PANEL_TEXT    = '#d8d9da';
export const PANEL_TEXT_DIM = '#9da0a4';

export const plotConfig = { responsive: true, displayModeBar: false };

// ---------------------------------------------------------------------------
// formatting
// ---------------------------------------------------------------------------

export function fmtNum(v, dp = 2) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const abs = Math.abs(v);
  const digits = abs >= 1000 ? 0 : abs >= 10 ? 1 : dp;
  return v.toLocaleString('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export function fmtPct(v, dp = 3) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return `${(v * 100).toFixed(dp)} %`;
}

export function fmtDateShort(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-GB', { hour12: false });
}

/** Calendar year of a stamp *in the dataset's own timezone*. The API returns
 *  UTC, so a naive getFullYear() reads 2027-12-31T22:00Z as 2027 when the run
 *  actually starts on 1 January 2028 SAST. */
export function yearOf(iso, tz) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const m = /^([+-])(\d{2}):?(\d{2})$/.exec(tz || '');
  const offMin = m ? (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3])) : 0;
  return new Date(d.getTime() + offMin * 60000).getUTCFullYear();
}

/** "2028–2067" from a dataset's ts_start / ts_end, both read in dataset tz. */
export function spanLabel(ds) {
  const a = yearOf(ds && ds.ts_start, ds && ds.tz);
  const b = yearOf(ds && ds.ts_end, ds && ds.tz);
  if (a === null || b === null) return null;
  return a === b ? `${a}` : `${a}–${b}`;
}

export const seriesLabel = (s) => s.display_name || [s.plant, s.resource].filter(Boolean).join(' · ') || `series ${s.id}`;

export const statusColor = (status) => {
  if (status === 'ready') return '#4ecca3';
  if (status === 'failed') return '#f87171';
  if (!status) return '#94a3b8';
  return '#fbbf24';   // staged / reconciling / loading / compressing / aggregating
};

/** ISO 8601 with the offset kept — the dataset is naive SAST read as +02:00,
 *  so a UTC round-trip would shift every stamp by two hours. */
export function isoLocal(d) {
  const p = (n) => String(n).padStart(2, '0');
  const off = -d.getTimezoneOffset();
  const sign = off >= 0 ? '+' : '-';
  const oh = p(Math.floor(Math.abs(off) / 60));
  const om = p(Math.abs(off) % 60);
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}${sign}${oh}:${om}`;
}

// ---------------------------------------------------------------------------
// widgets
// ---------------------------------------------------------------------------

export function NatureChip({ nature, count, active, onClick, title }) {
  const m = natureMeta(nature);
  const interactive = typeof onClick === 'function';
  return (
    <span
      className={`ds-chip${interactive ? ' ds-chip-btn' : ''}${active ? ' ds-chip-on' : ''}`}
      title={title || m.label}
      onClick={onClick}
      style={active || !interactive
        ? { background: m.color, color: '#0f172a', borderColor: m.color }
        : { borderColor: m.color, color: m.color }}
    >
      {m.label}{count === undefined || count === null ? '' : ` · ${count}`}
    </span>
  );
}

/**
 * Series picker shared by the Summary rail and the Explorer group panels.
 * Grouped nature → plant, with a search box. `selected` is an array of ids.
 */
export function SeriesTree({ series, selected, onChange, natureFilter, maxHeight = 320, footer }) {
  const [q, setQ] = useState('');
  const sel = useMemo(() => new Set(selected), [selected]);

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const pool = series.filter((s) => {
      if (natureFilter && natureFilter.length && !natureFilter.includes(s.nature)) return false;
      if (!needle) return true;
      return [s.plant, s.kategorie, s.resource, s.display_name]
        .filter(Boolean).some((t) => String(t).toLowerCase().includes(needle));
    });
    const byNature = new Map();
    pool.forEach((s) => {
      const n = s.nature || 'unassigned';
      if (!byNature.has(n)) byNature.set(n, new Map());
      const plants = byNature.get(n);
      const p = s.plant || '—';
      if (!plants.has(p)) plants.set(p, []);
      plants.get(p).push(s);
    });
    return NATURE_IDS
      .filter((n) => byNature.has(n))
      .map((n) => ({ nature: n, plants: [...byNature.get(n).entries()].map(([plant, rows]) => ({ plant, rows })) }));
  }, [series, q, natureFilter]);

  const toggle = (id) => {
    const next = new Set(sel);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange([...next]);
  };

  const toggleMany = (ids, on) => {
    const next = new Set(sel);
    ids.forEach((id) => { if (on) next.add(id); else next.delete(id); });
    onChange([...next]);
  };

  return (
    <div className="ds-tree">
      <input
        className="ds-input"
        placeholder="Search series…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <div className="ds-tree-body" style={{ maxHeight }}>
        {groups.length === 0 && <div className="ds-muted" style={{ padding: 8 }}>No series match.</div>}
        {groups.map((g) => {
          const ids = g.plants.flatMap((p) => p.rows.map((r) => r.id));
          const allOn = ids.every((id) => sel.has(id));
          return (
            <div key={g.nature} className="ds-tree-group">
              <div className="ds-tree-nature">
                <NatureChip nature={g.nature} count={ids.length} />
                <button className="ds-link" onClick={() => toggleMany(ids, !allOn)}>
                  {allOn ? 'none' : 'all'}
                </button>
              </div>
              {g.plants.map((p) => (
                <div key={p.plant} className="ds-tree-plant">
                  <div className="ds-tree-plant-name">{p.plant}</div>
                  {p.rows.map((s) => (
                    <label key={s.id} className="ds-tree-row" title={`${s.kategorie || ''} · ${s.resource || ''}`}>
                      <input type="checkbox" checked={sel.has(s.id)} onChange={() => toggle(s.id)} />
                      <span className="ds-tree-label">{seriesLabel(s)}</span>
                      {s.is_stock && <span className="ds-stock-flag" title="stock — never summed">S</span>}
                      <span className="ds-tree-unit">{s.unit || '—'}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          );
        })}
      </div>
      {footer}
    </div>
  );
}

/** Tiny inline sparkline. Deliberately not Plotly — one per summary column
 *  would mount dozens of charts. */
export function Sparkline({ values, color = '#4ecca3', width = 92, height = 22 }) {
  const pts = (values || []).filter((v) => v !== null && v !== undefined && !Number.isNaN(v));
  if (pts.length < 2) return <svg width={width} height={height} />;
  const min = Math.min(...pts);
  const max = Math.max(...pts);
  const span = max - min || 1;
  const step = width / (pts.length - 1);
  const d = pts.map((v, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${(height - 1 - ((v - min) / span) * (height - 2)).toFixed(1)}`).join(' ');
  return (
    <svg width={width} height={height} className="ds-spark" aria-hidden="true">
      <path d={d} fill="none" stroke={color} strokeWidth="1.2" />
    </svg>
  );
}

/** In-page confirm strip. Browser dialogs are not used anywhere in this app. */
export function ConfirmBar({ message, confirmLabel = 'Delete', onConfirm, onCancel, busy }) {
  return (
    <div className="ds-confirm">
      <span>{message}</span>
      <div style={{ flex: 1 }} />
      <button className="ds-btn-danger" onClick={onConfirm} disabled={busy}>
        {busy ? 'Working…' : confirmLabel}
      </button>
      <button className="btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
    </div>
  );
}
